<template>
	<v-dialog
		v-model="localShowDialog"
		:width="$vuetify.display.mobile ? '95%' : '500px'"
		:persistent="buttonIsLoading"
		@click:outside="handleDismiss"
		@keydown.esc="handleDismiss"
	>
		<v-card class="sgnl-pb-0">
			<v-card-title class="dialog-header sgnl-subtitle-medium"
				>Delete Account
				<v-spacer />
				<v-btn
					icon
					variant="text"
					size="small"
					@click="handleDismiss"
				>
					<Close16 />
				</v-btn>
			</v-card-title>
			<v-divider />
			<div class="dialog-body sgnl-paragraph-s-regular sgnl-text-color-secondary">
				<div>This will permanently delete:</div>
				<ul class="ul-list">
					<li><DotMark16 class="ul-icon" />Personal information associated with your account</li>
					<li><DotMark16 class="ul-icon" />Build artefacts, outputs, and metadata of tasks you have run</li>
					<li><DotMark16 class="ul-icon" />The C0-Cloud cores you have created</li>
					<li><DotMark16 class="ul-icon" />Files in your Signaloid Cloud Storage</li>
					<li><DotMark16 class="ul-icon" />Access permissions for any API integrations you have created</li>
				</ul>
				<AlertMessage type="error">
					You will not be able to access the platform afterwards unless you create a new account.
					<strong>This action is irreversible.</strong>
				</AlertMessage>
				<div class="input-group">
					<div>Type your email to confirm.</div>
					<v-text-field
						v-model="email"
						type="email"
						name="email"
						:placeholder="passedEmail"
						required
						hide-details="auto"
					/>
				</div>
				<v-alert
					v-if="showSupportMessage"
					density="compact"
					variant="outlined"
					type="warning"
					border="start"
				>
					{{ getSupportMessage }}
				</v-alert>
			</div>
			<v-divider />
			<v-card-actions class="dialog-footer">
				<v-btn
					variant="outlined"
					@click="handleDismiss"
				>
					Cancel
				</v-btn>
				<v-btn
					variant="flat"
					color="error"
					:loading="buttonIsLoading"
					:disabled="!readyToSubmit"
					@click="deleteAccount"
				>
					Delete Account
				</v-btn>
			</v-card-actions>
		</v-card>
	</v-dialog>
</template>

<script>
import { defineComponent } from "vue";
import Close16 from "@carbon/icons-vue/es/close/16";
import DotMark16 from "@carbon/icons-vue/es/dot-mark/16";
import AlertMessage from "@/components/Common/AlertMessage.vue";
import { useUserStore } from "@/stores/user";
import { sdk } from "@/js/sdk";
import { signOut } from "aws-amplify/auth";
import { useOutputsStore } from "@/stores/outputs";

const SupportMessage = `Please try again later.\n If this issue persists, please contact Signaloid Support at\n developer-support@signaloid.com. `;

export default defineComponent({
	name: "DeleteAccount",
	components: { Close16, DotMark16, AlertMessage },
	emits: ["update:showDialog", "delete-profile", "closeDeleteDialog"],
	data: () => ({
		buttonIsLoading: false,
		email: "",
		showSupportMessage: false,
	}),
	props: {
		showDialog: { type: Boolean, default: false },
		passedEmail: {
			type: String,
			default: null,
		},
	},
	computed: {
		localShowDialog: {
			get() {
				return this.showDialog;
			},
			set(value) {
				this.$emit("update:showDialog", value);
			},
		},
		readyToSubmit() {
			return this.email == this.passedEmail;
		},
		getSupportMessage() {
			return SupportMessage;
		},
	},
	setup() {
		const userStore = useUserStore();
		const taskOutputsStore = useOutputsStore();
		return { userStore, taskOutputsStore };
	},
	methods: {
		async deleteAccount() {
			this.buttonIsLoading = true;
			try {
				await sdk.users.delete(this.userStore.currentUser.userID);

				this.buttonIsLoading = false;
				this.$emit("delete-profile", "success");
				this.$posthog?.capture("account_deletion_initiated");
				await this.taskOutputsStore.purgeCache();
				await signOut();
				window.location.href = "/";
			} catch {
				this.buttonIsLoading = false;
				this.showSupportMessage = true;
				this.$emit("delete-profile", "failure");
			}
		},
		handleDismiss() {
			if (this.buttonIsLoading) {
				return;
			}
			this.closeDialog();
		},
		closeDialog() {
			this.$emit("closeDeleteDialog");
		},
	},
});
</script>
